/**
 * Company lookup utilities for efficient access to company data.
 * Provides memoized lookups for company information from companylistsearch.json.
 */

import companyList from '../data/companylistsearch.json';
import {
  getIndustryCategories,
  getCategoryAncestry,
  getSubIndustryCategories,
} from '../constants/filterCategories';

const resolveMarket = (company) => company?.market || company?.category || null;

export const DEFAULT_COMPANY_TICKER_FIELDS = [
  'Ticker',
  'ticker',
  'Company Symbol',
  'Company Mapping Ticker',
  'vendorTicker',
  'Vendor Ticker',
];

const DEFAULT_TAXONOMY_OUTPUT_FIELDS = {
  industry: 'Industry',
  subIndustry: 'Sub Industry',
  category: 'Category',
  market: 'Market',
};

export const COMPANY_TYPE_FILTERS = [
  { id: 'public', label: 'Public' },
  { id: 'venture', label: 'Venture' },
  { id: 'acquired', label: 'Acquired' },
  { id: 'unit', label: 'Software unit' },
];

export const COMPANY_OWNERSHIP_VALUES = {
  PUBLIC: 'Public',
  VC: 'VC',
  ACQUIRED: 'Acquired',
};

export const COMPANY_AI_PROFILE_VALUES = {
  AI_NATIVE: 'AI Native',
  NON_AI_NATIVE: 'Non-AI Native',
};

export const COMPANY_OWNERSHIP_OPTIONS = Object.values(COMPANY_OWNERSHIP_VALUES)
  .map((field) => ({ field }));

export const COMPANY_AI_PROFILE_OPTIONS = Object.values(COMPANY_AI_PROFILE_VALUES)
  .map((field) => ({ field }));

export const DEFAULT_COMPANY_OWNERSHIP_FILTER = Object.values(COMPANY_OWNERSHIP_VALUES);
export const DEFAULT_COMPANY_AI_PROFILE_FILTER = Object.values(COMPANY_AI_PROFILE_VALUES);

export const getCompanyType = (company) => {
  if (!company) return 'public';
  if (company.coverage === 'Acquired') return 'acquired';
  if (company.coverage === 'VC') return 'venture';
  if (company.entityKind === 'softwareUnit') return 'unit';
  return 'public';
};

export const filterCompaniesByType = (companies, selectedTypes) => {
  const selectedTypeSet = new Set(selectedTypes);
  if (!selectedTypeSet.size) return [];
  return companies.filter((company) => selectedTypeSet.has(company.companyType || getCompanyType(company)));
};

export const normalizeCompanyType = (companyType) => {
  const normalizedType = String(companyType || '').toLowerCase();
  if (normalizedType === 'acquired') return 'acquired';
  if (normalizedType === 'venture' || normalizedType === 'vc') return 'venture';
  if (normalizedType === 'unit') return 'unit';
  return 'public';
};

const COMPANY_TYPE_PALETTES = {
  public: {
    label: 'Public',
    light: { rgb: '63, 100, 168', accent: '#31558f' },
    dark: { rgb: '145, 166, 205', accent: '#adc2ed' },
  },
  venture: {
    label: 'Venture',
    light: { rgb: '42, 132, 96', accent: '#217a58' },
    dark: { rgb: '99, 190, 142', accent: '#8ce1af' },
  },
  acquired: {
    label: 'Acquired',
    light: { rgb: '184, 118, 28', accent: '#9c6418' },
    dark: { rgb: '232, 169, 70', accent: '#f2c36f' },
  },
  unit: {
    label: 'Software unit',
    light: { rgb: '118, 78, 170', accent: '#64409a' },
    dark: { rgb: '178, 150, 222', accent: '#cbb5f0' },
  },
};

const rgba = (rgb, opacity) => `rgba(${rgb}, ${opacity})`;

export const getCompanyTypeLabel = (companyType) => {
  const normalizedType = normalizeCompanyType(companyType);
  return COMPANY_TYPE_PALETTES[normalizedType].label;
};

export const getCompanyTypeTone = (companyType, theme) => {
  const normalizedType = normalizeCompanyType(companyType);
  const mode = theme?.palette?.mode === 'dark' ? 'dark' : 'light';
  const palette = COMPANY_TYPE_PALETTES[normalizedType][mode];
  const selectedFillOpacity = mode === 'dark' ? 0.24 : 0.16;

  return {
    type: normalizedType,
    label: COMPANY_TYPE_PALETTES[normalizedType].label,
    accent: palette.accent,
    border: rgba(palette.rgb, mode === 'dark' ? 0.36 : 0.34),
    background: rgba(palette.rgb, mode === 'dark' ? 0.09 : 0.07),
    hoverBackground: rgba(palette.rgb, mode === 'dark' ? 0.17 : 0.12),
    selectedBackground: rgba(palette.rgb, selectedFillOpacity),
    selectedHoverBackground: rgba(palette.rgb, mode === 'dark' ? 0.32 : 0.22),
  };
};

export const getCompanyTypeChipSx = (companyType, selected, theme) => {
  const tone = getCompanyTypeTone(companyType, theme);

  return {
    borderRadius: '6px',
    backgroundColor: selected ? tone.selectedBackground : tone.background,
    border: `1px solid ${selected ? tone.border : theme?.palette?.divider || tone.border}`,
    color: selected ? tone.accent : theme?.palette?.text?.secondary || tone.accent,
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: selected ? tone.selectedHoverBackground : tone.hoverBackground,
      borderColor: tone.border,
      color: tone.accent,
    },
  };
};

export const getCompanyTypeRowSx = (companyType, theme) => {
  const tone = getCompanyTypeTone(companyType, theme);

  return {
    backgroundColor: tone.background,
    borderLeft: `3px solid ${tone.border}`,
    '&:hover': {
      backgroundColor: tone.hoverBackground,
    },
  };
};

// ============================================
// Precomputed Lookup Maps (built once at import time)
// ============================================

/** Map of ticker -> full company object */
const companyByTicker = new Map();

/** Map of ticker -> market label (string) */
const categoryByTicker = new Map();

/** Map of ticker -> industry label */
const industryByTicker = new Map();

/** Map of ticker -> sub-industry label */
const subIndustryByTicker = new Map();

/** Map of ticker -> market label */
const marketByTicker = new Map();

/** Map of ticker -> aiNative flag */
const aiNativeByTicker = new Map();

/** Map of ticker -> taxonomy version */
const taxonomyVersionByTicker = new Map();

/** Map of ticker -> company name */
const nameByTicker = new Map();

/** Map of ticker -> isPrivate flag */
const privateByTicker = new Map();

/** Map of ticker -> coverage status */
const coverageByTicker = new Map();

/** Map of ticker -> current listing/status */
const currentStatusByTicker = new Map();

/** Map of ticker -> hasCapTable flag */
const capTableByTicker = new Map();

/** Map of ticker -> hasJobBoard flag */
const jobBoardByTicker = new Map();

/** Map of ticker -> hasDevBoard flag */
const devBoardByTicker = new Map();

/** Map of ticker -> guidance coverage flag */
const guidanceByTicker = new Map();

/** Map of ticker -> filing intelligence coverage flag */
const filingIntelligenceByTicker = new Map();

/** Map of ticker -> software unit parent and search aliases */
const softwareUnitByTicker = new Map();

// Build all lookup maps
(companyList || []).forEach((company) => {
  if (!company?.ticker) return;

  const ticker = String(company.ticker).toUpperCase();

  companyByTicker.set(ticker, company);
  categoryByTicker.set(ticker, resolveMarket(company));
  industryByTicker.set(ticker, company.industry || null);
  subIndustryByTicker.set(ticker, company.subIndustry || null);
  marketByTicker.set(ticker, resolveMarket(company));
  aiNativeByTicker.set(ticker, Boolean(company.aiNative));
  taxonomyVersionByTicker.set(ticker, company.taxonomyVersion || null);
  nameByTicker.set(ticker, company.companyName || company.name || ticker);
  privateByTicker.set(ticker, Boolean(company.isPrivate));
  coverageByTicker.set(ticker, company.coverage || null);
  currentStatusByTicker.set(ticker, company.currentStatus || null);
  capTableByTicker.set(ticker, Boolean(company.hasCapTable));
  jobBoardByTicker.set(ticker, Boolean(company.hasJobBoard));
  devBoardByTicker.set(ticker, Boolean(company.hasDevBoard));
  guidanceByTicker.set(ticker, Boolean(company.hasGuidance));
  filingIntelligenceByTicker.set(ticker, Boolean(company.hasFilingIntelligence));
  if (company.entityKind === 'softwareUnit') {
    softwareUnitByTicker.set(ticker, {
      parentTicker: company.parentTicker || null,
      parentName: company.parentName || null,
      aliases: Array.isArray(company.aliases) ? company.aliases : [],
    });
  }
});

// ============================================
// Lookup Functions
// ============================================

/**
 * Get full company object by ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {Object|null} Company object or null if not found
 */
export const getCompanyByTicker = (ticker) => {
  return companyByTicker.get(String(ticker).toUpperCase()) || null;
};

/**
 * Get category label (string) by ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string|null} Category label or null if not found
 */
export const getCategoryByTicker = (ticker) => {
  return categoryByTicker.get(String(ticker).toUpperCase()) ?? null;
};

export const getIndustryByTicker = (ticker) => {
  return industryByTicker.get(String(ticker).toUpperCase()) ?? null;
};

export const getSubIndustryByTicker = (ticker) => {
  return subIndustryByTicker.get(String(ticker).toUpperCase()) ?? null;
};

export const getMarketByTicker = (ticker) => {
  return marketByTicker.get(String(ticker).toUpperCase()) ?? null;
};

export const getCanonicalCompanyTaxonomyByTicker = (ticker) => {
  const company = getCompanyByTicker(ticker);
  if (!company) return null;

  return {
    ticker: String(company.ticker || ticker).toUpperCase(),
    industry: company.industry || null,
    subIndustry: company.subIndustry || null,
    category: company.category || null,
    market: resolveMarket(company),
    taxonomyVersion: company.taxonomyVersion || null,
  };
};

export const resolveCompanyTickerFromRow = (
  row,
  tickerFields = DEFAULT_COMPANY_TICKER_FIELDS
) => {
  if (!row || typeof row !== 'object') return null;

  for (const field of tickerFields) {
    const value = row?.[field];
    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim().toUpperCase();
    }
  }

  return null;
};

export const enrichCompanyRowWithMappedTaxonomy = (row, options = {}) => {
  if (!row || typeof row !== 'object') return row;

  const ticker = options.ticker || resolveCompanyTickerFromRow(row, options.tickerFields);
  const taxonomy = getCanonicalCompanyTaxonomyByTicker(ticker);
  if (!taxonomy) return row;

  const outputFields = {
    ...DEFAULT_TAXONOMY_OUTPUT_FIELDS,
    ...(options.outputFields || {}),
  };
  const updates = {};

  [
    ['industry', taxonomy.industry],
    ['subIndustry', taxonomy.subIndustry],
    ['category', taxonomy.category],
    ['market', taxonomy.market],
  ].forEach(([key, value]) => {
    const field = outputFields[key];
    if (field && value !== null && value !== undefined) updates[field] = value;
  });

  const hasChanges = Object.entries(updates).some(([field, value]) => row[field] !== value);
  return hasChanges ? { ...row, ...updates } : row;
};

export const enrichCompanyRowsWithMappedTaxonomy = (rows, options = {}) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => enrichCompanyRowWithMappedTaxonomy(row, options));
};

export const isAiNativeByTicker = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  if (!aiNativeByTicker.has(normalized)) return null;
  return aiNativeByTicker.get(normalized);
};

export const getCompanyOwnershipByTicker = (ticker) => {
  const normalized = String(ticker || '').trim().toUpperCase();
  if (!companyByTicker.has(normalized)) return null;
  if (coverageByTicker.get(normalized) === 'Acquired') {
    return COMPANY_OWNERSHIP_VALUES.ACQUIRED;
  }
  if (coverageByTicker.get(normalized) === 'VC' || privateByTicker.get(normalized)) {
    return COMPANY_OWNERSHIP_VALUES.VC;
  }
  return COMPANY_OWNERSHIP_VALUES.PUBLIC;
};

export const getCompanyAiProfileByTicker = (ticker) => {
  const isAiNative = isAiNativeByTicker(ticker);
  if (isAiNative === null) return null;
  return isAiNative
    ? COMPANY_AI_PROFILE_VALUES.AI_NATIVE
    : COMPANY_AI_PROFILE_VALUES.NON_AI_NATIVE;
};

const matchesCompanyDimension = (value, selectedValues, defaultValues) => {
  const selected = Array.isArray(selectedValues) ? selectedValues : defaultValues;
  if (selected.length === 0) return false;
  if (value !== null) return selected.includes(value);
  return defaultValues.every((option) => selected.includes(option));
};

export const matchesCompanyCohort = (
  ticker,
  {
    ownership = DEFAULT_COMPANY_OWNERSHIP_FILTER,
    aiProfile = DEFAULT_COMPANY_AI_PROFILE_FILTER,
  } = {}
) => (
  matchesCompanyDimension(
    getCompanyOwnershipByTicker(ticker),
    ownership,
    DEFAULT_COMPANY_OWNERSHIP_FILTER
  )
  && matchesCompanyDimension(
    getCompanyAiProfileByTicker(ticker),
    aiProfile,
    DEFAULT_COMPANY_AI_PROFILE_FILTER
  )
);

/**
 * Get company name by ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string|null} Company name or null if not found
 */
export const getCompanyNameByTicker = (ticker) => {
  return nameByTicker.get(String(ticker).toUpperCase()) || null;
};

/**
 * Check if company is private by ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {boolean|null} True if private, false if public, null if not found
 */
export const isPrivateByTicker = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  if (!privateByTicker.has(normalized)) return null;
  return privateByTicker.get(normalized);
};

const PRIVATE_COMPANY_ID_PATTERN = /^PVT-[A-Z0-9][A-Z0-9-]*$/i;
const PRIVATE_COMPANY_ID_IN_LABEL_PATTERN = /\bPVT-[A-Z0-9][A-Z0-9-]*\b/i;

/**
 * Return whether a ticker is an internal private-company identifier.
 * Catalog metadata is authoritative; the prefix check prevents unknown private
 * ids from leaking while coverage data is being updated.
 */
export const isPrivateCompanyIdentifier = (ticker) => {
  const normalized = String(ticker || '').trim().toUpperCase();
  if (!normalized) return false;
  return privateByTicker.get(normalized) === true || PRIVATE_COMPANY_ID_PATTERN.test(normalized);
};

/** Resolve the customer-facing company name without exposing a private id. */
export const getCompanyDisplayName = (ticker, candidateName, fallback = 'Private company') => {
  const normalized = String(ticker || '').trim().toUpperCase();
  const suppliedName = String(candidateName || '').trim();
  const catalogName = nameByTicker.get(normalized);
  const safeSuppliedName = suppliedName
    && suppliedName.toUpperCase() !== normalized
    && !PRIVATE_COMPANY_ID_IN_LABEL_PATTERN.test(suppliedName)
      ? suppliedName
      : null;
  const resolvedName = safeSuppliedName || catalogName;

  if (resolvedName) return resolvedName;
  if (isPrivateCompanyIdentifier(normalized)) return fallback;
  return normalized || fallback;
};

/**
 * Format a company identifier for presentation while retaining public tickers.
 * publicStyle: "nameTicker" | "ticker" | "name"
 */
export const formatCompanyDisplayLabel = ({
  ticker,
  companyName,
  publicStyle = 'nameTicker',
  fallback = 'Private company',
} = {}) => {
  const normalized = String(ticker || '').trim().toUpperCase();
  const name = getCompanyDisplayName(normalized, companyName, fallback);

  if (isPrivateCompanyIdentifier(normalized)) return name;
  if (publicStyle === 'name') return name;
  if (publicStyle === 'ticker') return normalized || name;
  return normalized && name !== normalized ? `${name} (${normalized})` : name;
};

/** Private companies are searchable by name only; public tickers remain aliases. */
export const matchesCompanySearch = (option, query) => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return true;

  const ticker = String(option?.ticker || '').trim();
  const companyName = getCompanyDisplayName(ticker, option?.companyName || option?.name);
  if (companyName.toLowerCase().includes(normalizedQuery)) return true;
  // Software units also match their aliases ("GCP") and parent ("Amazon").
  const unit = softwareUnitByTicker.get(ticker.toUpperCase());
  if (unit && [...unit.aliases, unit.parentName, unit.parentTicker]
    .some((term) => term && String(term).toLowerCase().includes(normalizedQuery))) return true;
  return !isPrivateCompanyIdentifier(ticker) && ticker.toLowerCase().includes(normalizedQuery);
};

/**
 * Software unit parent and aliases by ticker (null for ordinary companies).
 * @param {string} ticker - Stock ticker symbol
 * @returns {{parentTicker: string|null, parentName: string|null, aliases: string[]}|null}
 */
export const getSoftwareUnitByTicker = (ticker) => softwareUnitByTicker.get(String(ticker).toUpperCase()) || null;

/**
 * Get private company type by ticker.
 * @param {string} ticker - Stock ticker symbol
 * @returns {'acquired'|'vc'|'public'|null}
 */
export const getPrivateCompanyTypeByTicker = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  if (!companyByTicker.has(normalized)) return null;
  if (coverageByTicker.get(normalized) === 'Acquired') return 'acquired';
  if (!privateByTicker.get(normalized)) return 'public';
  return 'vc';
};

/**
 * Check if a company is an acquired subsidiary.
 * @param {string} ticker - Stock ticker symbol
 * @returns {boolean|null} True if acquired subsidiary, false otherwise, null if not found
 */
export const isAcquiredSubsidiaryByTicker = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  if (!companyByTicker.has(normalized)) return null;
  return coverageByTicker.get(normalized) === 'Acquired' && currentStatusByTicker.get(normalized) === 'Subsidiary';
};

/**
 * Check if company has cap table by ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {boolean|null} True if has cap table, false otherwise, null if not found
 */
export const hasCapTableByTicker = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  if (!capTableByTicker.has(normalized)) return null;
  return capTableByTicker.get(normalized);
};

/**
 * Check if company has job board by ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {boolean|null} True if has job board, false otherwise, null if not found
 */
export const hasJobBoardByTicker = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  if (!jobBoardByTicker.has(normalized)) return null;
  return jobBoardByTicker.get(normalized);
};

/**
 * Check if company has dev board by ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {boolean|null} True if has dev board, false otherwise, null if not found
 */
export const hasDevBoardByTicker = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  if (!devBoardByTicker.has(normalized)) return null;
  return devBoardByTicker.get(normalized);
};

/**
 * Get all company info by ticker in one call
 * @param {string} ticker - Stock ticker symbol
 * @returns {Object} Object with all company properties (category is a string label)
 */
export const getCompanyInfo = (ticker) => {
  const normalized = String(ticker).toUpperCase();
  const isAcquiredSubsidiary = isAcquiredSubsidiaryByTicker(normalized);

  return {
    ticker: normalized,
    companyName: nameByTicker.get(normalized) || null,
    industry: industryByTicker.get(normalized) ?? null,
    subIndustry: subIndustryByTicker.get(normalized) ?? null,
    category: categoryByTicker.get(normalized) ?? null,
    market: marketByTicker.get(normalized) ?? null,
    aiNative: aiNativeByTicker.get(normalized) ?? null,
    taxonomyVersion: taxonomyVersionByTicker.get(normalized) ?? null,
    isPrivate: privateByTicker.get(normalized) ?? null,
    coverage: coverageByTicker.get(normalized) ?? null,
    currentStatus: currentStatusByTicker.get(normalized) ?? null,
    isAcquiredSubsidiary,
    privateCompanyType: getPrivateCompanyTypeByTicker(normalized),
    hasCapTable: capTableByTicker.get(normalized) ?? null,
    hasJobBoard: jobBoardByTicker.get(normalized) ?? null,
    hasDevBoard: devBoardByTicker.get(normalized) ?? null,
    hasGuidance: guidanceByTicker.get(normalized) ?? null,
    hasFilingIntelligence: filingIntelligenceByTicker.get(normalized) ?? null,
    isSoftwareUnit: softwareUnitByTicker.has(normalized),
    softwareUnit: softwareUnitByTicker.get(normalized) || null,
    raw: companyByTicker.get(normalized) || null,
  };
};

// ============================================
// Category Helpers
// ============================================

/**
 * Get news category array for a given company category label.
 * Returns sibling labels + sub-industry label for news filtering.
 * @param {string} categoryLabel - Company category label (e.g. 'HR, Finance, Legal & SCM')
 * @returns {string[]} Array of category labels for news filtering
 */
export const getNewsCategoryArray = (categoryLabel) => {
  const ancestry = getCategoryAncestry(categoryLabel);
  if (!ancestry) return [categoryLabel];

  const { industry, subIndustry } = ancestry;

  if (subIndustry === 'Diversified Software') {
    return getIndustryCategories('Software');
  }

  // Return sub-industry name + all sibling leaves
  const siblings = getSubIndustryCategories(industry, subIndustry);
  return [subIndustry, ...siblings];
};

// ============================================
// Filtering Helpers
// ============================================

/**
 * Get all companies in specified categories
 * @param {string[]} categoryLabels - Array of category label strings
 * @returns {Object[]} Array of company objects
 */
export const getCompaniesByCategories = (categoryLabels) => {
  const labelSet = new Set(categoryLabels);
  return companyList.filter((c) => {
    const label = resolveMarket(c);
    return label && labelSet.has(label);
  });
};

/**
 * Get all public companies
 * @returns {Object[]} Array of public company objects
 */
export const getPublicCompanies = () => {
  return companyList.filter((c) => !c.isPrivate);
};

/**
 * Get all private companies
 * @returns {Object[]} Array of private company objects
 */
export const getPrivateCompanies = () => {
  return companyList.filter((c) => c.isPrivate);
};

/**
 * Get all companies with job boards
 * @returns {Object[]} Array of company objects with job boards
 */
export const getCompaniesWithJobBoards = () => {
  return companyList.filter((c) => c.hasJobBoard);
};

// ============================================
// Search/Autocomplete Helpers
// ============================================

/**
 * Get formatted options for company autocomplete
 * @returns {Object[]} Array of company search option objects
 */
export const getCompanyAutocompleteOptions = () => {
  return companyList.map((c) => ({
    label: c.companyName || c.name || c.ticker,
    companyName: c.companyName || c.name || c.ticker,
    ticker: c.ticker,
    companyType: getCompanyType(c),
    hasDevBoard: Boolean(c.hasDevBoard),
    hasGuidance: Boolean(c.hasGuidance),
    hasFilingIntelligence: Boolean(c.hasFilingIntelligence),
  }));
};

/**
 * Search companies by name or ticker
 * @param {string} query - Search query
 * @param {number} limit - Maximum results (default: 10)
 * @returns {Object[]} Matching company objects
 */
export const searchCompanies = (query, limit = 10) => {
  if (!query || typeof query !== 'string') return [];

  const q = query.trim().toLowerCase();
  if (!q) return [];

  const results = [];

  for (const company of companyList) {
    const name = (company.companyName || company.name || '').toLowerCase();
    const ticker = (company.ticker || '').toLowerCase();

    if (ticker === q || name === q) {
      results.unshift(company); // Exact matches first
    } else if (ticker.includes(q) || name.includes(q)) {
      results.push(company);
    }

    if (results.length >= limit) break;
  }

  return results.slice(0, limit);
};

// Export the raw company list for cases where direct access is needed
export { companyList };
